import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCapturedResearchAddress } from '@/utils/researchAddressBootstrap';

/** Mirrors the backend's Field(max_length=128) on ChatRequest (#1599) — rejects abuse at the URL
 * boundary. */
const MAX_RESEARCH_PARAM_LENGTH = 128;

/** Larger than the ID cap to fit full US addresses; still bounded to reject hostile links. */
const MAX_ADDRESS_PARAM_LENGTH = 256;

// Drop rather than truncate — a truncated token would look valid but link to the wrong participant.
const sanitizeResearchParam = (value: string | null): string | null => {
  if (value === null) return null;
  if (value.length === 0) return null;
  if (value.length > MAX_RESEARCH_PARAM_LENGTH) return null;
  return value;
};

// Clamp rather than drop — a partial address may still resolve against Places API.
const sanitizeAddressParam = (value: string | null): string | null => {
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return trimmed.slice(0, MAX_ADDRESS_PARAM_LENGTH);
};

export interface ResearchParams {
  /** ?campaignCode= — research panel identifier, sent to the backend and persisted on the user
   * record. */
  campaignCode: string | null;
  /** ?participantId= — opaque per-participant token sent to the backend; provides linkage
   * user_id='guest' cannot. */
  participantId: string | null;
  /** ?campaign= — legacy lowercase param kept for the existing Mixpanel event in App.tsx; not
   * sent to the backend. */
  legacyCampaign: string | null;
  /** ?address= — client-only pre-fill hint for the guest landing; re-validated against Places
   * before any ballot lookup. */
  address: string | null;
}

/** Reads research-collaboration URL params from the current location. Absent params return null. */
const useResearchParams = (): ResearchParams => {
  const [searchParams] = useSearchParams();

  return useMemo<ResearchParams>(
    () => ({
      // legacyCampaign intentionally unbounded — Mixpanel only, never reaches the backend.
      campaignCode: sanitizeResearchParam(searchParams.get('campaignCode')),
      participantId: sanitizeResearchParam(searchParams.get('participantId')),
      legacyCampaign: searchParams.get('campaign'),
      // Read the captured pre-fill hint rather than the live URL: it is
      // stripped from the URL before analytics load (see
      // researchAddressBootstrap) so a home address never reaches Mixpanel /
      // browser history. campaignCode/participantId stay in the URL (non-PII).
      address: sanitizeAddressParam(getCapturedResearchAddress()),
    }),
    [searchParams]
  );
};

export default useResearchParams;
